import { api } from "@/lib/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  AlertTriangle,
  BarChart2,
  Bell,
  Calendar,
  CheckCircle2,
  CheckSquare,
  Clock,
  Database,
  Info,
  Loader2,
  MessageSquare,
  Shield,
  Video,
  X,
} from "lucide-react";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

interface Notification {
  pk: string;
  sk: string;
  id?: string;
  title: string;
  body?: string;
  message?: string;
  type: string;
  read: boolean;
  link?: string;
  createdAt: string;
}

interface NotificationPanelProps {
  isOpen: boolean;
  onToggle: () => void;
}

export function NotificationPanel({ isOpen, onToggle }: NotificationPanelProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // ── Queries ──
  const { data: notifications = [], isLoading } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => api.getList<Notification>("/notifications"),
    refetchInterval: 15_000,
  });

  // Client-side deduplication guard
  const uniqueNotifications = useMemo(() => {
    const seen = new Set<string>();
    return notifications.filter((notif) => {
      const text = notif.body || notif.message || "";
      const key = `${notif.title}#${text}#${notif.link || ""}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [notifications]);

  const { data: unreadCount = 0 } = useQuery({
    queryKey: ["notifications", "unread-count"],
    queryFn: async () => {
      const res = await api.get<{ count: number }>("/notifications/unread-count");
      return res.count;
    },
    refetchInterval: 15_000,
  });

  // ── Mutations ──
  const markAsRead = useMutation({
    mutationFn: (sk: string) => api.patch(`/notifications/${encodeURIComponent(sk)}/read`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      queryClient.invalidateQueries({ queryKey: ["notifications", "unread-count"] });
    },
  });

  const markAllRead = useMutation({
    mutationFn: () => api.patch("/notifications/read-all", {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      queryClient.invalidateQueries({ queryKey: ["notifications", "unread-count"] });
    },
  });

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "critical_ticket_alert":
      case "task_overdue":
      case "error":
        return { Icon: AlertCircle, color: "text-red-500 bg-red-50 border-red-200" };
      case "task_due_soon":
      case "stale_task_alert":
      case "app_checklist_reminder":
      case "warning":
        return { Icon: AlertTriangle, color: "text-amber-500 bg-amber-50 border-amber-200" };
      case "task_assigned":
      case "ticket_assigned":
        return { Icon: CheckSquare, color: "text-blue-500 bg-blue-50 border-blue-200" };
      case "task_comment":
      case "ticket_comment":
        return { Icon: MessageSquare, color: "text-indigo-500 bg-indigo-50 border-indigo-200" };
      case "demo_reminder":
        return { Icon: Video, color: "text-purple-500 bg-purple-50 border-purple-200" };
      case "weekly_digest":
        return { Icon: BarChart2, color: "text-emerald-500 bg-emerald-50 border-emerald-200" };
      case "backup_created":
        return { Icon: Database, color: "text-cyan-500 bg-cyan-50 border-cyan-200" };
      case "team_role_changed":
        return { Icon: Shield, color: "text-violet-500 bg-violet-50 border-violet-200" };
      case "sprint_ending_soon":
      case "task_status_changed":
      case "ticket_status_changed":
        return { Icon: Clock, color: "text-amber-600 bg-amber-50 border-amber-200" };
      case "idea_submitted":
        return { Icon: Calendar, color: "text-teal-500 bg-teal-50 border-teal-200" };
      default:
        return { Icon: Info, color: "text-blue-500 bg-blue-50 border-blue-200" };
    }
  };

  const handleNotificationClick = (notif: Notification) => {
    if (!notif.read) {
      markAsRead.mutate(notif.sk);
    }
    onToggle();
    if (notif.link) {
      navigate(notif.link);
    }
  };

  return (
    <div className="relative">
      {/* Bell Button */}
      <button
        onClick={onToggle}
        className="relative p-2 rounded-lg text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
        title="Centro de notificaciones"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-bold leading-none text-white transform translate-x-1/3 -translate-y-1/3 bg-red-600 rounded-full border-2 border-white shadow-sm">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-40" onClick={onToggle} />

          <div className="absolute right-0 mt-2 w-[calc(100vw-2rem)] sm:w-96 bg-white rounded-xl shadow-xl border border-zinc-200 z-50 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-zinc-50 border-b border-zinc-200">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-bold text-zinc-900">Notificaciones</h3>
                {unreadCount > 0 && (
                  <span className="px-2 py-0.5 text-xs font-semibold bg-blue-100 text-blue-700 rounded-full">
                    {unreadCount} sin leer
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={() => markAllRead.mutate()}
                    disabled={markAllRead.isPending}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium disabled:opacity-50 transition-colors"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Marcar leídas
                  </button>
                )}
                <button
                  onClick={onToggle}
                  className="p-1 rounded-md text-zinc-400 hover:text-zinc-600 hover:bg-zinc-200/50 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Notification List */}
            <div className="max-h-96 overflow-y-auto divide-y divide-zinc-100">
              {isLoading ? (
                <div className="flex items-center justify-center py-10 text-zinc-400">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : uniqueNotifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-zinc-400">
                  <Bell className="w-8 h-8 mb-2 opacity-50" />
                  <p className="text-sm font-medium">Sin notificaciones por ahora</p>
                  <p className="text-xs text-zinc-400 mt-1">
                    Las novedades del equipo aparecerán aquí
                  </p>
                </div>
              ) : (
                uniqueNotifications.map((notif: Notification) => {
                  const { Icon, color } = getNotificationIcon(notif.type);
                  const contentText = notif.body || notif.message || "";
                  const dateStr = notif.createdAt ? notif.createdAt.slice(0, 16).replace("T", " ") : "";

                  return (
                    <div
                      key={notif.sk}
                      onClick={() => handleNotificationClick(notif)}
                      className={`flex items-start gap-3 px-4 py-3 hover:bg-zinc-50 transition-colors cursor-pointer ${
                        !notif.read ? "bg-blue-50/40" : ""
                      }`}
                    >
                      <div className={`p-1.5 rounded-lg border flex-shrink-0 mt-0.5 ${color}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p
                            className={`text-sm ${
                              !notif.read ? "font-semibold text-zinc-900" : "font-medium text-zinc-700"
                            }`}
                          >
                            {notif.title}
                          </p>
                          {!notif.read && (
                            <span className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0" />
                          )}
                        </div>
                        {contentText && (
                          <p className="text-xs text-zinc-600 mt-0.5 line-clamp-3 leading-relaxed">
                            {contentText}
                          </p>
                        )}
                        <p className="text-[10px] font-medium text-zinc-400 mt-1.5 flex items-center gap-1">
                          <Clock className="w-3 h-3 text-zinc-300" />
                          {dateStr}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
